import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ExternalHeaderComponent } from '../../../oex-ui-kit/components/external-header/external-header.component';
import { HeaderComponent, IMenu } from '../../../oex-ui-kit/components/header/header';
import { ModalService } from '../../../oex-ui-kit/services/modal.service';
import { NotificationsService } from '../../../oex-ui-kit/services/notifications.service';
import { ProgressService } from '../../../oex-ui-kit/services/progress.service';
import { FooterComponent } from '../../controls/footer/footer.component';
import { APIService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';

/* const resolveNotifications: ResolveFn<IHeaderNotification[]> = async (route: ActivatedRouteSnapshot) => {
  const api = inject(APIService);
  return await api.getNotifications();
} */

const REFRESH_COINS_INTERVAL = 30 * 60 * 1000; // 30 minutes

@Component({
  selector: 'cld-main-page',
  imports: [
    ExternalHeaderComponent,
    HeaderComponent,
    RouterOutlet,
    FooterComponent
  ],
  templateUrl: './main-page.component.html',
  styleUrl: './main-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainPageComponent implements OnDestroy {
  protected readonly api = inject(APIService);
  protected readonly auth = inject(AuthService);
  private readonly ns = inject(NotificationsService);
  private readonly ps = inject(ProgressService);
  //protected readonly resolveNotifications = resolveNotifications;
  private readonly header = viewChild.required(HeaderComponent);

  menuItems = signal<IMenu[]>([
    {
      id: 'coins',
      title: (this.auth.user?.balance || 0).toString(),
      url: '/portal/billing',
      icon: '/img/icons/icon-coins.svg',
      useRouteForActiveState: true,
      condition: () => this.auth.isLogged,
    },
    {
      id: 'profile',
      title: 'Profile',
      showAvatar: true,
      hideOnMobile: true,
      titleDesc: (this.auth?.user?.name || '') + (this.auth.isImpersonating() ? ' (Impersonating)' : ''),
      useRouteForActiveState: true,
      profileUrl: '/portal/overview',
      condition: () => this.auth.isLogged,
      items: [
        {
          title: 'Overview',
          url: '/portal/overview',
        },
        /*{
          title: 'Notifications',
          url: '/portal/notifications',
        },*/
        {
          title: 'Deployments',
          url: '/portal/deployments',
        },
        {
          title: 'Billing',
          url: '/portal/billing',
        },
        {
          title: 'My profile',
          url: '/portal/profile',
        },
        {
          title: 'Exit impersonating mode',
          style: 'link',
          separator: true,
          condition: () => this.auth.isImpersonating(),
          click: () => void this.auth.exitImpersonation()
        },
        {
          title: 'Log Out',
          style: 'link',
          separator: true,
          condition: () => !this.auth.isImpersonating(),
          click: () => this.auth.logout()
        }
      ],
      buttons: [
        {
          id: 'logout',
          title: 'Log out',
          condition: () => this.auth.isLogged,
          click: () => this.auth.logout()
        }
      ]
    }
  ]);
  protected readonly animatedRoutes = this.menuItems()
    .filter(mi => {
      return (!!mi.url) && (!mi.url?.startsWith('/portal') && !mi.url?.startsWith('/admin'))
    })
    .map(m => m.url ?? '');

  protected readonly notifications: IMenu = {
    id: 'notifications',
    title: 'Notifications',
    useRouteForActiveState: true,
  };
  protected readonly ms = inject(ModalService);
  protected readonly router = inject(Router);
  protected isFooter = signal(false);
  private readonly menu = viewChild(HeaderComponent);
  private readonly routerEvents = this.router.events.pipe(takeUntilDestroyed());
  private onLogout = this.auth.onLogout.pipe(takeUntilDestroyed());
  private onBalanceChanged = this.auth.onBalanceChanged.pipe(takeUntilDestroyed());
  private refreshCoinsInterval: any;

  ngOnInit() {
    this.subscribeToLogout();
    this.subscribeForRouteChange();
    this.initCoinsRefreshInterval();
    this.onBalanceChanged.subscribe(balance => {
      this.updateMenu(balance);
    });

    // Subscribe for notification events
    this.ns.onMarkAsRead.subscribe(ids => {
      void this.markAsRead(ids);
    });
  }

  updateMenu(balance?: number) {
    const items = this.menuItems();
    if (items) {
      const profile = items.find(m => m.id === 'profile');
      if (profile) {
        // Update user name
        profile.titleDesc = this.auth?.user?.name || '';
      }

      const coins = items.find(m => m.id === 'coins');
      if (coins) {
        // Update user balance
        coins.title = balance === undefined ? (this.auth.user?.balance || 0).toString() : (balance || 0).toString();
      }
    }
    this.menuItems.set([...this.menuItems()]);
    this.menu()?.refresh();
  }

  protected onRegister() {
    this.auth.register();
  }

  protected onLogin() {
    void this.auth.login();
  }

  private subscribeForRouteChange() {
    this.routerEvents.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.checkFooterVisibility();
      }
    });

    // Manually trigger the check on initial load
    this.checkFooterVisibility();
  }

  private checkFooterVisibility() {
    let hideFooter = false;
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    // Navigate to the deepest child route
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Check the deepest route and its parents for hideFooter
    while (route) {
      if (route.routeConfig?.data?.hideFooter) {
        hideFooter = true;
        break;
      }
      route = route.parent;
    }

    this.isFooter.set(!hideFooter);
  }

  private subscribeToLogout() {
    this.onLogout.subscribe(() => {
      this.stopCoinsRefreshInterval();
      this.updateMenu();
      // Check for portal or admin panel
      const r = this.router.url.split('/')[1];
      if (r === 'admin' || r === 'portal') {
        void this.router.navigateByUrl('/');
      }
    });
  }

  private stopCoinsRefreshInterval() {
    clearInterval(this.refreshCoinsInterval);
  }

  private initCoinsRefreshInterval() {
    /*  if (!this.auth.isLogged) {
       return;
     }
     this.refreshCoinsInterval = setInterval(async () => {
       if (!this.auth.isLogged) {
         this.stopCoinsRefreshInterval();
         return;
       }
       try {
         const resp = await this.api.refreshPoints();
         resp.user.points = 200;
         if (resp.user.points !== this.auth.user?.points) {
           this.updateMenu(resp.user.points);
         }
       } catch { }
     }, REFRESH_COINS_INTERVAL); */
  }

  private markAsRead(ids: number[]) {
    void this.ps.runWithProgress(async () => {
      // await this.api.markNotificationsAsRead(ids);
      if (this.auth.user?.notifications) {
        this.auth.user.notifications -= ids.length;
        this.header().refresh();
      }
    });
  }

  ngOnDestroy() {
    this.stopCoinsRefreshInterval();
  }
}
